import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CustomInput from "../../../shared/utils/Input";
import Button from "@mui/material/Button";
import { InputLabel } from "@mui/material";
import { Select } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { updateFormField } from "../../../redux/slices/vendorFormSlice";
import { verifyGST } from "../../../shared/utils/gstInfoApi";
import { setFieldError } from "../../../redux/slices/vendorFormSlice";
import { statecityData } from "../../../shared/enums";
import Styles from "../../../styles/vendorDashboard.module.css";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import useFetchCities from "./useFetchCities";
import CircularProgress from "@mui/material/CircularProgress";
import { Cross } from "../../../shared/assets/icons";
import { setIsGstVerified } from "../../../redux/slices/vendorFormSlice";
import { validateOrganizationDetails } from "./vendorValidation";
import { VerifiedTick } from "../../../shared/assets/icons";
import capitalizeWords from "../../../helperFunctions/capitalizedWords";

const OrganizationDetailForm = ({ onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  // const [isGstVerified, setIsGstVerified] = useState(false);
  const dispatch = useDispatch();
  const { formData, errors, isGstVerified } = useSelector(
    (state) => state.vendorForm
  );

  const gstin = formData?.GSTIN;

  // call everytime when the gst length is 15
  useEffect(() => {
    if (gstin && gstin.length === 15) {
      fetchDetailsThroughGSTIN(gstin.toUpperCase());
    }
  }, [gstin]);
  const cityApiKey = process.env.REACT_APP_FETCH_STATE_CITY_API_KEY;
  const cityArray = useFetchCities(formData?.state, statecityData, cityApiKey);

  // GST APi
  const fetchDetailsThroughGSTIN = async (gstNumber) => {
    setLoading(true);

    const responseData = await verifyGST(gstNumber);
    setLoading(false);
    console.log(responseData);

    if (responseData.error) {
      dispatch(
        setFieldError({ name: "GSTIN", message: "Enter a valid GSTIN" })
      );
      dispatch(setIsGstVerified(false));
      return;
    }

    const taxpayerInfo = responseData?.taxpayerInfo?.pradr?.addr || {};

    dispatch(updateFormField({ name: "GSTIN", value: gstNumber }));
    dispatch(
      updateFormField({ name: "state", value: taxpayerInfo.stcd || "" })
    );
    dispatch(
      updateFormField({ name: "pinCode", value: taxpayerInfo.pncd || "" })
    );
    dispatch(
      updateFormField({
        name: "vendorBusinessName",
        value: capitalizeWords(responseData?.taxpayerInfo?.tradeNam) || "",
      })
    );

    const addressParts = [
      taxpayerInfo.flno,
      taxpayerInfo.bno,
      taxpayerInfo.st,
      taxpayerInfo.landMark,
      taxpayerInfo.locality,
      taxpayerInfo.loc,
    ].filter(Boolean);

    dispatch(
      updateFormField({
        name: "address",
        value: capitalizeWords(addressParts.join(", ")),
      })
    );

    dispatch(
      updateFormField({
        name: "city",
        value: taxpayerInfo.dst || "",
      })
    );

    dispatch(setIsGstVerified(true));
  };

  //Update the form field in redux
  const handleChange = (e) => {
    const { name, value } = e.target;

    // only for GST to verify if length is 15 or to covert it into uppercase
    if (name === "GSTIN") {
      if (value.length > 15) {
        return;
      }

      const upperGSTIN = value.toUpperCase();
      // Dispatch the uppercase GSTIN
      if (!upperGSTIN.startsWith(" ")) {
        dispatch(updateFormField({ name, value: upperGSTIN }));
      }

      return;
    }

    if (name === "yearsInBusiness") {
      // Allow only empty string (for clearing) or positive integers
      if (value === "" || /^[0-9]+$/.test(value)) {
        dispatch(updateFormField({ name, value }));
      }
      return;
    }

    if (!value.startsWith(" ")) {
      dispatch(updateFormField({ name, value }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    // when use leave the the input onblur it shows this errors
    const validationMessages = {
      yearsInBusiness: "Years in business can not be empty.",
      vendorBusinessName: "Organization name is required.",
      address: "Organization's address is required.",
      state: "State is required.",
      city: "City is required.",
      pinCode: "Pincode is required",
    };

    if (name === "GSTIN") {
      if (value.length !== 15) {
        dispatch(
          setFieldError({
            name,
            message: "Enter a valid 15-digit number",
          })
        );
      }
    }

    if (validationMessages[name]) {
      if (!value.trim()) {
        dispatch(setFieldError({ name, message: validationMessages[name] }));
      } else {
        dispatch(setFieldError({ name, message: "" })); // clear error
      }
    }

    if (name === "ownerPrimaryContactNo") {
      if (!/^\d{10}$/.test(value)) {
        dispatch(
          setFieldError({ name, message: "Enter a valid 10-digit number" })
        );
      }
    }

    // if (saveButtonClicked) {
    //   dispatch(validateAllFields());
    // }
  };

  const handleSubmit = () => {
    // dispatch(setSaveButtonClicked(true));
    const isValid = validateOrganizationDetails(formData, dispatch);

    if (isValid) {
      console.log("Submitting", formData);
      onNext();
    }
  };

  return (
    <div className=" max-w-md ">
      <div
        onClick={onBack}
        className=" w-fit p-1 px-1.5 mb-4 rounded-full bg-gray-100 cursor-pointer"
      >
        <ArrowBackIcon fontSize="small" />
      </div>
      <div>
        <h1 className="text-3xl font-bold mb-4">Organization Details</h1>
        <p className="mb-6">
          Please tell us more about your organisation to personalise your data
          and for seamless interaction.
        </p>
      </div>
      <div
        className="overflow-auto"
        style={{ maxHeight: "calc(100vh - 200px)" }}
      >
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Grid container spacing={2}>
            {/* GSTIN field */}
            <Grid item xs={12}>
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <CustomInput
                  placeholder="Enter GSTIN"
                  size="small"
                  title="GSTIN *"
                  name="GSTIN"
                  value={formData.GSTIN}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.GSTIN}
                  errorMessage={errors.GSTIN}
                />
                {loading ? (
                  <div
                    style={{
                      position: "absolute",
                      top: "70%",
                      right: "10px",
                      transform: "translateY(-50%)",
                      width: "20px",
                      height: "20px",
                      zIndex: 9999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress color="inherit" size={20} />
                  </div>
                ) : errors.GSTIN ? (
                  <div
                    style={{
                      position: "absolute",
                      top: "53%",
                      right: "10px",
                      transform: "translateY(-50%)",
                      width: "20px",
                      height: "20px",
                      zIndex: 9999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Cross
                      color="var(--error-color)"
                      fillcolor="var(--error-color)"
                    />
                  </div>
                ) : isGstVerified ? (
                  <div
                    style={{
                      position: "absolute",
                      top: "70%",
                      right: "10px",
                      transform: "translateY(-50%)",
                      width: "20px",
                      height: "20px",
                      zIndex: 9999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <VerifiedTick
                      color="var(--third-color)"
                      fillcolor="var(--third-color)"
                    />
                  </div>
                ) : null}
              </div>
            </Grid>
            {/* Organization name */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter Organization Name"
                title="Organization Name *"
                size="small"
                name="vendorBusinessName"
                required
                value={formData?.vendorBusinessName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.vendorBusinessName}
                errorMessage={errors.vendorBusinessName}
              />
            </Grid>
            {/* organization address */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter Organization Address"
                title="Address *"
                size="small"
                name="address"
                required
                value={formData?.address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.address}
                errorMessage={errors.address}
              />
            </Grid>
            {/* landmark */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter Landmark"
                title="Landmark"
                size="small"
                name="landmark"
                value={formData?.landmark}
                onChange={handleChange}
              />
            </Grid>
            {/* State */}
            <Grid item xs={12}>
              <InputLabel
                className={`${Styles.labelColor} ${
                  errors?.state
                    ? "text-[var(--error-color)]"
                    : "text-[var(--heading-color)]"
                }`}
              >
                State *
              </InputLabel>
              <FormControl
                fullWidth
                size="small"
                sx={{ marginTop: "0.5rem" }}
                error={errors.state}
                errorMessage={
                  errors.state ? (
                    <span style={{ color: "var(--error-color)" }}>
                      Please Enter State
                    </span>
                  ) : null
                }
                value={formData?.state.name || formData?.state}
              >
                <Select
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Set border color
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on focus
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on hover
                    },
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#b0b0b0", // Ensure hover state is consistent
                      },
                    },
                  }}
                  style={{ color: formData?.state ? "" : "#a2a2a2" }}
                  displayEmpty
                  name="state"
                  onBlur={handleBlur}
                  value={formData?.state}
                  onChange={handleChange}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <p className="text-[1rem] text-[var(--light-gray)]">
                          State
                        </p>
                      );
                    }

                    return selected;
                  }}
                >
                  {statecityData.map((state) => (
                    <MenuItem key={state.name} value={state.name}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.state && (
                <p className="font-normal text-[0.75rem] leading-[1.66] tracking-[0.03em] text-left mt-[4px] mr-[14px] mb-0 ml-[14px] text-[var(--error-color)]">
                  {errors.state}
                </p>
              )}
              {/* <CustomInput
              placeholder="Enter State"
              size="small"
              name="state"
              required
              value={formData?.state}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.state}
              errorMessage={errors.state}
            /> */}
            </Grid>
            {/* city */}
            <Grid item xs={12}>
              <InputLabel
                className={`${Styles.labelColor} ${
                  errors?.city
                    ? "text-[var(--error-color)]"
                    : "text-[var(--heading-color)]"
                }`}
              >
                City *
              </InputLabel>
              <FormControl
                fullWidth
                size="small"
                sx={{ marginTop: "0.5rem" }}
                error={errors.city}
                errorMessage={
                  errors.city ? (
                    <span style={{ color: "var(--error-color)" }}>
                      Please Enter City
                    </span>
                  ) : null
                }
                value={formData?.city}
              >
                <Select
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Set border color
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on focus
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b0b0b0", // Keep border color on hover
                    },
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#b0b0b0", // Ensure hover state is consistent
                      },
                    },
                  }}
                  style={{ color: formData?.state ? "" : "#a2a2a2" }}
                  displayEmpty
                  name="city"
                  onBlur={handleBlur}
                  value={formData?.city}
                  onChange={handleChange}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <p className="text-[1rem] text-[var(--light-gray)]">
                          City
                        </p>
                      );
                    }

                    return selected;
                  }}
                >
                  {cityArray.map((city) => (
                    <MenuItem key={city.id} value={city.name}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {errors.state && (
                <p className="font-normal text-[0.75rem] leading-[1.66] tracking-[0.03em] text-left mt-[4px] mr-[14px] mb-0 ml-[14px] text-[var(--error-color)]">
                  {errors.city}
                </p>
              )}
            </Grid>
            {/* Pincode */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter pincode"
                title="Pincode *"
                size="small"
                name="pinCode"
                required
                value={formData?.pinCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.pinCode}
                errorMessage={errors.pinCode}
              />
            </Grid>
            {/* Years in business */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter years in business"
                title="Years In Business *"
                size="small"
                name="yearsInBusiness"
                required
                value={formData?.yearsInBusiness}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.yearsInBusiness}
                errorMessage={errors.yearsInBusiness}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter Website URL"
                title="Website URL "
                size="small"
                name="website"
                required
                value={formData?.website}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.website}
                errorMessage={errors.website}
              />
            </Grid>
          </Grid>
        </Box>

        <div className=" mt-4 flex justify-end">
          <Button onClick={handleSubmit} variant="contained">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationDetailForm;
